
import { APIDataDepartment } from '@evertel/types'
import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { Api } from '@evertel/api'
import { uniqBy } from 'lodash'


class DepartmentsSearchController {
    departments: APIDataDepartment[] = []
    departmentsLatestFetch: APIDataDepartment[] = []
    departmentsCount = 0
    searchTerm = ''
    page = 1
    limit = 50
    order = 'name ASC'
    infiniteScroll = false
    _filter = {}
    whereFilter: Record<string, any> = {}

    constructor(
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init(infiniteScroll: boolean) {
        this.infiniteScroll = infiniteScroll
        this.resetSearch()
    }

    setSearchTerm(value: string) {
        this.resetSearchMeta()
        this.searchTerm = value
    }

    setPage(pageNumber: number) {
        this.page = pageNumber
    }

    setFilter(extraFilter: Record<string, any>) {
        this._filter = extraFilter
    }

    setWhereFilter(extraFilter: Record<string, any>) {
        this.whereFilter = extraFilter
    }

    setLimit(limit: number) {
        this.limit = limit
    }

    setOrder(newOrder: string) {
        if (this.order === newOrder) return
        this.resetSearchMeta()
        this.order = newOrder
    }

    search = async () => {
        let count = this.departmentsCount
        let newDepartments = []

        // grab the total count of rooms for this search (once each new search)
        if (!this.departmentsCount) {
            count = await this.fetchDepartmentsCount()
        }

        runInAction(() => {
            this.departmentsLatestFetch = []
        })

        // stop if we already fetched them all
        if (this.departments?.length < count) {

            try {
                newDepartments = await this.api.Routes.Department.get(this.filter)
            } catch (e) {
                console.error('ERROR: /libs/shared/department/DepartmentsSearchController search()', e)
                throw e
            }
        }

        runInAction(() => {
            this.departmentsLatestFetch = newDepartments
            this.departments = (this.infiniteScroll) ? uniqBy([...this.departments, ...newDepartments], 'id') : newDepartments
        })

        return this.departmentsLatestFetch

    }

    async fetchDepartmentsCount() {
        let count = 0

        try {

            count = (await this.api.Routes.Department.getCount(this.filter.where)).count

        } catch (e) {
            console.log('ERROR: /libs/shared/department/DepartmentsSearchController fetchDepartmentsCount()', e)
            throw e
        }

        runInAction(() => {
            this.departmentsCount = count ?? 0
        })

        return this.departmentsCount
    }

    loadNextPage(): void {
        this.page = this.page + 1
        this.search()
    }

    resetSearchMeta(): void {
        this.departments = []
        this.departmentsCount = 0
        this.page = 1
    }

    resetSearch(): void {
        this.resetSearchMeta()
        this.searchTerm = ''
        this.order = 'name ASC'
        this.limit = 50
    }

    get filter() {
        const andConditions: Record<string, any>[] = [
            { hidden: false },
            this.whereFilter || {}
        ]

        if (this.searchTerm) {
            andConditions.push({
                or: [
                    { city: { like: `%${this.searchTerm}%` } },
                    { county: { like: `%${this.searchTerm}%` } },
                    { name: { like: `%${this.searchTerm}%` } },
                    { type: { like: `%${this.searchTerm}%` } },
                    { state: { like: `%${this.searchTerm}%` } },
                    { federal: { like: `%${this.searchTerm}%` } }
                ]
            })
        }

        return {
            where: {
                and: andConditions
            },
            ...this._filter,
            fields: { options: false },
            order: this.order,
            skip: (this.page - 1) * this.limit,
            limit: this.limit
        }
    }
}

decorate(injectable(), DepartmentsSearchController)
decorate(inject(Api), DepartmentsSearchController, 0)

export { DepartmentsSearchController }
